import React from 'react';
import { useIntl } from 'react-intl';
import type { MailboxProviderId } from 'fym-shared/src/music24/outreach/mailboxProviders';
import { TypedMemo } from '../../../common/types';
import { appPasswordLength, appPasswordLetters, cleanAppPassword } from './mailboxGuideState';
import { mailboxGuideMessages as messages } from './mailboxGuideMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the guide; each part uses its own slice of it */
import styles from './MailboxGuide.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface AppPasswordFieldProps {
  providerId: MailboxProviderId;
  value: string;
  onChange: (value: string) => void;
}

/**
 * The paste field of an app password (design outreach-v2 o2 step 3). Spaces go away as the person
 * pastes ("abcd efgh ijkl mnop"); for Google, Apple, Yahoo and AOL the field counts the 16 letters.
 */
const AppPasswordFieldComponent: React.FC<AppPasswordFieldProps> = ({ providerId, value, onChange }) => {
  const intl = useIntl();
  const length = appPasswordLength(providerId);
  const letters = appPasswordLetters(value);
  const status = () => {
    if (length === null || letters === 0) {
      return <span className={styles.pasteCount}>{intl.formatMessage(messages.appPasswordSpaces)}</span>;
    }
    return letters === length ? (
      <span className={styles.pasteOk}>{intl.formatMessage(messages.appPasswordOk, { length })}</span>
    ) : (
      <span className={styles.pasteCount}>
        {intl.formatMessage(messages.appPasswordCount, { count: letters, length })}
      </span>
    );
  };
  return (
    <div className={styles.paste}>
      <input
        type="text"
        className="form-control"
        value={value}
        placeholder="abcd efgh ijkl mnop"
        autoComplete="off"
        spellCheck={false}
        aria-label={intl.formatMessage(messages.appPasswordLabel)}
        onChange={(event) => onChange(cleanAppPassword(event.target.value))}
      />
      {status()}
    </div>
  );
};

export const AppPasswordField = TypedMemo(AppPasswordFieldComponent);
